import type { ActionFunctionArgs, LoaderFunctionArgs } from "react-router";
import { redirect } from "react-router";
import { Form, useActionData, useLoaderData } from "react-router";
import React from "react";
import {
  isValidServiceType,
  PharmacistFormContentStep2,
  type PharmacistStep2Prefill,
} from "~/features/landing/PharmacistFormContentStep2";
import { postApiParnerPreregistrationList } from "~/utils/apis/postApiPartnerPreregistrationList";

export async function loader({ request }: LoaderFunctionArgs) {
  const url = new URL(request.url);
  const { searchParams } = url;

  return {
    prefill: {
      partner_name: searchParams.get("partner_name") ?? undefined,
      salutation: getPrefillSalutation(searchParams.get("salutation")),
      title: searchParams.get("title") ?? undefined,
      firstName: searchParams.get("firstName") ?? undefined,
      lastName: searchParams.get("lastName") ?? undefined,
      email: searchParams.get("email") ?? undefined,
      street: searchParams.get("street") ?? undefined,
      zip: searchParams.get("zip") ?? undefined,
      city: searchParams.get("city") ?? undefined,
      country: getPrefillCountry(searchParams.get("country")),
      telephone: searchParams.get("telephone") ?? undefined,
      referral_source: searchParams.get("referral_source") ?? undefined,
      referral_source_other: searchParams.get("referral_source_other") ?? undefined,
      apozin_short_code: searchParams.get("apozin_short_code") ?? undefined,
      misc_info: searchParams.get("misc_info") ?? undefined,
      interested_service_types: getInterestedServiceTypes(searchParams.get("interested_service_types")),
    } satisfies PharmacistStep2Prefill,
  };
}

export default function Pharmacy() {
  const [loading, setLoading] = React.useState(true);
  const { prefill } = useLoaderData<typeof loader>();
  React.useEffect(() => {
    setLoading(false);
  }, []);

  const error = useActionData<typeof action>();

  if (loading) return null;

  return (
    <Form method="post" className="flex flex-col">
      <PharmacistFormContentStep2 hasSecondStep={false} error={error} prefill={prefill} />
    </Form>
  );
}

export async function action({ request }: ActionFunctionArgs) {
  try {
    await postApiParnerPreregistrationList(request);
  } catch (e) {
    return e as Response;
  }

  return redirect("/register/email_sent");
}

function getPrefillSalutation(value: string | null) {
  return value === "Herr" || value === "Frau" ? value : undefined;
}

function getPrefillCountry(value: string | null) {
  return value && /^[A-Z]{2}$/.test(value) ? value : undefined;
}

function getInterestedServiceTypes(value: string | null) {
  if (!value) return [];

  try {
    const parsed = JSON.parse(value);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (serviceType): serviceType is string => typeof serviceType === "string" && isValidServiceType(serviceType),
    );
  } catch {
    return [];
  }
}
